import type {
  CriticalResultsDisplayStatus,
  CriticalResultsCriticalitySummaryItem,
} from '../../types/criticalResultsDashboard.types';

export type CriticalResultsAcknowledgementDisplayStatus = 'Acknowledged' | 'Unacknowledged';

export function resolveAcknowledgementDisplayStatus(alert: {
  displayStatus: CriticalResultsDisplayStatus;
  status?: string | null;
  acknowledgementStatus?: string | null;
}): CriticalResultsAcknowledgementDisplayStatus {
  if (
    alert.displayStatus === 'Acknowledged' ||
    alert.status === 'Acknowledged' ||
    alert.acknowledgementStatus === 'Acknowledged'
  ) {
    return 'Acknowledged';
  }

  return 'Unacknowledged';
}

const CRITICALITY_BAND_ORDER = ['Major', 'Minor'] as const;

export function normalizeCriticalitySummary(
  items: CriticalResultsCriticalitySummaryItem[]
): CriticalResultsCriticalitySummaryItem[] {
  const byBand = new Map(items.map(item => [String(item.criticalityBand), item]));

  return CRITICALITY_BAND_ORDER.map(band => {
    const existing = byBand.get(band);
    if (existing) {
      return existing;
    }

    return {
      criticalityBand: band,
      total: 0,
      open: 0,
      acknowledged: 0,
      breaches: 0,
      averageTatSeconds: 0,
    };
  });
}

export function formatShortDuration(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    return '0m';
  }
  const minutes = Math.round(totalSeconds / 60);
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remMin = minutes % 60;
    return remMin > 0 ? `${hours}h ${remMin}m` : `${hours}h`;
  }
  return `${minutes}m`;
}

export function formatSinceNotified(startedAt?: string | null): string {
  if (!startedAt) {
    return '—';
  }
  const started = new Date(startedAt).getTime();
  if (Number.isNaN(started)) {
    return '—';
  }
  const elapsedSeconds = Math.max(0, Math.floor((Date.now() - started) / 1000));
  return formatShortDuration(elapsedSeconds);
}

export function formatCriticalityLabel(band?: string | null): string {
  if (!band) {
    return '—';
  }
  if (band === 'Major') {
    return 'Critical-Major';
  }
  if (band === 'Minor') {
    return 'Critical-Minor';
  }
  return band;
}

export function criticalityBadgeClass(band?: string | null): string {
  if (band === 'Major') {
    return 'crd-badge crd-badge--major';
  }
  if (band === 'Minor') {
    return 'crd-badge crd-badge--minor';
  }
  return 'crd-badge';
}

export function displayStatusLabel(
  status:
    | CriticalResultsDisplayStatus
    | CriticalResultsAcknowledgementDisplayStatus
    | 'Open'
    | 'Pending'
): string {
  switch (status) {
    case 'Acknowledged':
      return 'Acknowledged';
    case 'Unacknowledged':
      return 'Unacknowledged';
    case 'Open':
      return 'Open';
    case 'Pending':
      return 'Pending ack';
    case 'Escalating':
      return 'Escalating';
    case 'Breached':
      return 'Breached';
    case 'Closed':
      return 'Closed';
    default:
      return status;
  }
}

export function displayStatusClass(
  status:
    | CriticalResultsDisplayStatus
    | CriticalResultsAcknowledgementDisplayStatus
    | 'Open'
    | 'Pending'
): string {
  switch (status) {
    case 'Acknowledged':
      return 'crd-status crd-status--acknowledged';
    case 'Unacknowledged':
      return 'crd-status crd-status--pending';
    case 'Open':
      return 'crd-status crd-status--open';
    case 'Pending':
      return 'crd-status crd-status--pending';
    case 'Escalating':
      return 'crd-status crd-status--escalating';
    case 'Breached':
      return 'crd-status crd-status--breached';
    case 'Closed':
      return 'crd-status crd-status--closed';
    default:
      return 'crd-status';
  }
}

export function isoDateStart(date: string): string {
  if (!date) {
    return '';
  }
  return `${date}T00:00:00.000Z`;
}

export function isoDateEnd(date: string): string {
  if (!date) {
    return '';
  }
  return `${date}T23:59:59.999Z`;
}

export function monthStartIsoDate(reference = new Date()): string {
  const year = reference.getFullYear();
  const month = String(reference.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
}

export function todayIsoDate(reference = new Date()): string {
  const year = reference.getFullYear();
  const month = String(reference.getMonth() + 1).padStart(2, '0');
  const day = String(reference.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatLastRefreshed(date: Date | null): string {
  if (!date) {
    return '—';
  }
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function formatLastRefreshedShort(date: Date | null): string {
  if (!date) {
    return 'Last refreshed —';
  }
  const time = date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
  return `Last refreshed ${time}`;
}

export function formatLastUpdatedLabel(date: Date | null): string {
  if (!date) {
    return 'Last updated: —';
  }
  const time = date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const day = date.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' });
  return `Last updated: ${day}, ${time}`;
}

export function formatRefreshIntervalLabel(seconds: number): string {
  if (seconds < 60) {
    return `${seconds} sec`;
  }
  if (seconds % 60 === 0) {
    const minutes = seconds / 60;
    return minutes === 1 ? '1 min' : `${minutes} min`;
  }
  return `${seconds} sec`;
}
